import {
    NotificationSubscriptionGroupBaseResponse,
    NotificationSubscriptionGroupBaseResponseUnion,
    NotificationType,
    PagedResultsModel,
} from '@kcftech/prometheustypescriptapiclient';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { buildOrderBy, Filter, toDataSourceResult } from 'utils/query_v3';
import { Modify } from 'utils/types';

export type GroupedNotificationSubscriptionsFilter = Readonly<{
    corporationId?: string;
    locationId?: string;
    nameSearch?: string;
    notificationTypes?: NotificationType[];
}>;

export const buildFilterExpression = ({
    corporationId,
    locationId,
    notificationTypes,
}: Partial<GroupedNotificationSubscriptionsFilter>) =>
    Filter.and(
        corporationId && Filter.eq('CorporationId', corporationId),
        locationId && Filter.eq('LocationId', locationId),
        (notificationTypes &&
            notificationTypes.length > 0 &&
            Filter.in('NotificationType', notificationTypes)) ||
            undefined
    );

export type UseGroupedNotificationSubscriptionsDataSourceOptions = BaseDataSourceOptions<
    NotificationSubscriptionGroup,
    GroupedNotificationSubscriptionsFilter
>;

export type GroupedNotificationSubscriptionsPagedDataSource = PagedDataSource<
    NotificationSubscriptionGroup,
    GroupedNotificationSubscriptionsFilter
>;

export type NotificationSubscriptionGroup = NotificationSubscriptionGroupBaseResponseUnion;

const getAllGroupedNotificationSubscriptions: DataSourceQuery<
    NotificationSubscriptionGroup,
    GroupedNotificationSubscriptionsFilter
> = ({ filter: { nameSearch, ...filterRest }, sort, page, pageLimit }) => {
    const filter = buildFilterExpression(filterRest);
    const orderBy = buildOrderBy(sort);
    return toDataSourceResult(
        (sdClient.getNotificationSubscriptionGroups({
            filter,
            page,
            pageLimit,
            orderBy,
            nameSearch,
        }) as unknown) as Promise<
            Modify<
                PagedResultsModel<NotificationSubscriptionGroupBaseResponse>,
                {
                    Result: NotificationSubscriptionGroup[];
                }
            >
        >
    );
};

export function usePagedGroupedNotificationSubscriptionsDataSource({
    ...options
}: UseGroupedNotificationSubscriptionsDataSourceOptions): GroupedNotificationSubscriptionsPagedDataSource {
    return usePagedDataSource({
        ...options,
        query: getAllGroupedNotificationSubscriptions,
    });
}
